<template>
  <div :class="[$style.config, $style.wrap, computedIsActive && $style.active]">
    <div :class="$style.icon">
      <picture v-if="computedIcon" :class="$style.iconPicture">
        <img
          :class="$style.iconImage"
          :src="computedIcon.url"
          :height="computedIcon.height"
          :width="computedIcon.width"
          loading="lazy"
        />
      </picture>
    </div>
    <div :class="$style.header">{{ computedHeader }}</div>
    <MarkedParser :class="$style.copy" :md="computedCopy" />
  </div>
</template>

<script setup lang="ts">
import type { TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse } from '~/types/contentful'

const props = defineProps<{
  data: TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse
  activeSlide: TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse
}>()

const computedCopy = computed(() => props.data?.fields?.tileCopy)
const computedHeader = computed(() => props.data?.fields?.tileHeader)
const computedIconDefault = computed(() => {
  const url = props.data?.fields?.tileIconDefault?.fields?.file?.url
  const width =
    props.data?.fields?.tileIconDefault?.fields?.file?.details?.image?.width ||
    0
  const height =
    props.data?.fields?.tileIconDefault?.fields?.file?.details?.image?.height ||
    0

  if (!url) return null
  return { height, url, width }
})
const computedIconActive = computed(() => {
  const url = props.data?.fields?.tileIconActive?.fields?.file?.url
  const width =
    props.data?.fields?.tileIconActive?.fields?.file?.details?.image?.width || 0
  const height =
    props.data?.fields?.tileIconActive?.fields?.file?.details?.image?.height ||
    0
  if (!url) return null
  return { height, url, width }
})
const computedIsActive = computed(
  () => props.data?.sys?.id === props.activeSlide?.sys?.id,
)
const computedIcon = computed(() => {
  if (!computedIconDefault.value) return null
  if (!computedIconActive.value) return computedIconDefault.value
  return computedIsActive.value
    ? computedIconActive.value
    : computedIconDefault.value
})
</script>

<style lang="scss" module>
.config {
  @include media('md') {
    --wrap-align-items: start;
    --wrap-border-radius: 15px;
    --wrap-grid-columns: 1fr;
    --wrap-grid-rows: max-content max-content 1fr;
    --wrap-gap: #{to-rem(14px)};
    --wrap-padding: #{to-rem(20px)} #{to-rem(17px)};
    --wrap-text-align: center;

    --icon-margin: 0 0 #{to-rem(23px)};

    --icon-picture-dimension: 62px;

    --header-font-size: #{to-rem(22px)};
    --header-font-weight: bold;

    --copy-display: block;
  }

  @include media('lg') {
    --wrap-padding: #{to-rem(40px)} #{to-rem(17px)};
  }

  &.active {
    --wrap-bg-color: var(--purple);

    --copy-color: var(--gray-1);
  }
}

.wrap {
  align-items: var(--wrap-align-items, center);
  background-color: var(--wrap-bg-color, transparent);
  border: var(--wrap-border, 1px solid var(--purple));
  border-radius: var(--wrap-border-radius, 6px);
  cursor: pointer;
  display: grid;
  grid-template-columns: var(--wrap-grid-columns, 25px 1fr);
  grid-template-rows: var(--wrap-grid-rows, 1fr);
  gap: var(--wrap-gap, to-rem(11px));
  height: 100%;
  padding: var(--wrap-padding, to-rem(9px) to-rem(13px));
  text-align: var(--wrap-text-align, left);
  transition: background-color 0.5s ease;
}

.icon {
  margin: var(--icon-margin, 0);
}

.iconPicture {
  display: block;
  height: var(--icon-picture-dimension, 23px);
  margin: 0 auto;
  width: var(--icon-picture-dimension, 23px);
}

.iconImage {
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.header {
  color: var(--copy-color, var(--gray-8));
  font-size: var(--header-font-size, to-rem(14px));
  font-family: var(--roboto-condensed);
  font-weight: var(--header-font-weight, normal);
  text-transform: capitalize;
}

.copy {
  display: var(--copy-display, none);
  font-weight: 600;
  font-size: to-rem(16px);
  line-height: math.div(22, 16);
  text-align: center;
  color: var(--copy-color, var(--gray-8));
}
</style>
